package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.dto.tips.MyTipShareDto
import com.evoapps.evoshift.data.dto.tips.TipPoolDto
import com.evoapps.evoshift.data.dto.tips.TipShareDto
import com.evoapps.evoshift.data.remote.CloudFunctionsClient
import com.evoapps.evoshift.data.remote.CloudFunctionsException
import com.evoapps.evoshift.domain.model.MyTipShare
import com.evoapps.evoshift.domain.model.TipException
import com.evoapps.evoshift.domain.model.TipPeriod
import com.evoapps.evoshift.domain.model.TipPool
import com.evoapps.evoshift.domain.model.TipPoolDraft
import com.evoapps.evoshift.domain.model.TipShare
import com.evoapps.evoshift.domain.repository.TipRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject

/**
 * SH-124 tips over Firebase: managers listen to workplaces/{wpId}/tip_pools, a person to their own
 * tip_shares (userId == theirs, as the rules require); saving and removing go through the
 * saveTipPool / deleteTipPool functions (functions/tips.js), which split on the server.
 */
class TipRepositoryImpl(
    private val firestore: AppFirestore,
    private val functions: CloudFunctionsClient
) : TipRepository {
    private val json = Json { ignoreUnknownKeys = true }

    private fun workplace(workplaceId: String) = firestore.collection("workplaces").document(workplaceId)

    override fun observePools(workplaceId: String): Flow<List<TipPool>> =
        workplace(workplaceId).collection("tip_pools").safeSnapshots
            .map { snap -> snap.documents.map { toDomain(it.data<TipPoolDto>()) }.sortedByDescending { it.startDate } }
            .distinctUntilChanged()

    override fun observeMyShares(userId: String, workplaceId: String): Flow<List<MyTipShare>> =
        workplace(workplaceId).collection("tip_shares").where { "userId" equalTo userId }.safeSnapshots
            .map { snap -> snap.documents.map { toDomain(it.data<MyTipShareDto>()) }.sortedByDescending { it.startDate } }
            .distinctUntilChanged()

    override suspend fun savePool(draft: TipPoolDraft): Result<TipPool> = call {
        val result = functions.call("saveTipPool", buildJsonObject {
            put("workplaceId", draft.workplaceId)
            put("period", draft.period.name)
            put("startDate", draft.startDate)
            put("amountMinor", draft.amountMinor)
            putJsonObject("roleWeights") { draft.roleWeights.forEach { (role, weight) -> put(role, weight) } }
        })
        toDomain(json.decodeFromJsonElement(TipPoolDto.serializer(), result))
    }

    override suspend fun deletePool(workplaceId: String, poolId: String): Result<Unit> = call {
        functions.call("deleteTipPool", buildJsonObject {
            put("workplaceId", workplaceId)
            put("poolId", poolId)
        })
        Unit
    }

    /** A function's refusal with a reason becomes [TipException]; anything else stays as it is. */
    private suspend fun <T> call(block: suspend () -> T): Result<T> = try {
        Result.success(block())
    } catch (e: CancellationException) {
        throw e
    } catch (e: CloudFunctionsException) {
        val reason = ((e.details as? JsonObject)?.get("reason") as? JsonPrimitive)?.contentOrNull
        Result.failure(if (reason != null && TipException.Reason.of(reason) != TipException.Reason.UNKNOWN) TipException(TipException.Reason.of(reason)) else e)
    } catch (e: Exception) {
        Result.failure(e)
    }

    internal companion object {
        fun periodOf(name: String) = TipPeriod.entries.find { it.name == name } ?: TipPeriod.DAY

        fun toDomain(dto: TipShareDto) = TipShare(dto.userId, dto.displayName, dto.role, dto.minutesWorked, dto.amountMinor)

        fun toDomain(dto: TipPoolDto) = TipPool(
            id = dto.id,
            workplaceId = dto.workplaceId,
            period = periodOf(dto.period),
            startDate = dto.startDate,
            endDate = dto.endDate,
            amountMinor = dto.amountMinor,
            currency = dto.currency,
            roleWeights = dto.roleWeights,
            shares = dto.shares.map(::toDomain),
            updatedAt = dto.updatedAt,
            updatedBy = dto.updatedBy,
            source = dto.source
        )

        fun toDomain(dto: MyTipShareDto) = MyTipShare(
            poolId = dto.poolId,
            workplaceId = dto.workplaceId,
            period = periodOf(dto.period),
            startDate = dto.startDate,
            endDate = dto.endDate,
            amountMinor = dto.amountMinor,
            currency = dto.currency,
            minutesWorked = dto.minutesWorked
        )
    }
}
